import { useState, useEffect, useContext } from "react";
import { Alert, Button } from "react-bootstrap";
import StateContext from "../../../context/State";
import PortalDataService from "../../../services/portal.service";
import { RPWDataReader, getDataReader } from "../../../../../livedataFuncs";
import { useAppSelector } from "../../../../../redux/hooks";
import { lddStateObject } from "../../../../../redux/reducer";
import { FileInfoModel } from "../../../types";
import { lddGlobalSessionInfo } from "../../../../navigationTypes";
import { getIncludeFirmEmail } from "../../../utils/includeFirmEmail";
import languageMap from "../../../../../../../ClientPortal/src/langs.json";

const EmailLog = () => {
    const [emailLogs, setEmailLogs] = useState([]);
    const [notificationVisible, setNotificationVisible] = useState();
    const [dashboardFields, setDashboardFields] = useState({});
    const [isNotaryFirm, setIsNotaryFirm] = useState<boolean>(false);
    const [language, setLanguage] = useState<string>('EN');

    const { state, update_resentInvitation, dashboardFieldsPollVersion } = useContext(StateContext);
    const portalService = new PortalDataService(state);
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    let DataReaderObj: RPWDataReader;
    DataReaderObj = getDataReader(livedataRootContainer.dataRoot);
    const sessionInfo: lddGlobalSessionInfo = useAppSelector((state) => state.SessionInfo);

    const labels = [
        "addIntakeFields",
        "documentsRequested",
        "retainerAgreement",
        "clientConnectId",
        "sourceOfFunds",
        "remoteClosingCalls"
    ]

    const nonCustomizedEmailFields = [
        "retainerAgreement",
        "sourceOfFunds"
    ]

    useEffect(() => {
        portalService.getFileInfo()
            .then((data: FileInfoModel) => {
                const { file } = data;
                setIsNotaryFirm(file.isNotaryFirm);
                const lang = DataReaderObj.getLanguage()
                setLanguage(lang)
            })
            .catch(err => { console.log(err) });
    }, []);


    useEffect(() => {
        async function loadEmailLogs() {
            try {
                const result = await portalService.fetchEmailLogs(state.appId);
                if (result.status === 'success') {
                    setEmailLogs(result.data);
                } else {
                    throw new Error(result.error);
                }
            } catch (e) {

            }
        }

        loadEmailLogs();
    }, [update_resentInvitation]);

    useEffect(() => {
        const newFields = DataReaderObj.getDashboardFields();
        if (newFields) {
            setDashboardFields(newFields);
            setNotificationVisible(newFields.isDirty);
        }
    }, [livedataRootContainer, dashboardFieldsPollVersion]);

    const handleNotifyClient = async () => {
        const portal: any = await portalService.getPortalByAppId(state.appId);
        const portalId: any = portal[0]['Id'];
        if (portalId < "") {
            return;
        }
        const firmName = DataReaderObj.getFirmName();
        const dirtyFields = [];

        for (const lab of labels) {
            if (dashboardFields[lab + 'isDirty'] === "1") {
                dirtyFields.push(lab);
            }
        }


        const code = await portalService.getShortCode(portalId);
        const province = await portalService.getProvince()
        const codeVal = await code.shortCode;
        DataReaderObj.setDashboardIsDirty();
        // Get the buyer's information for the email
        const emailInfos: string[] = DataReaderObj.getClientContactInfo(province);

        const sid = sessionInfo.sid;
        const firmIncluded = await getIncludeFirmEmail(sid);
        const firmContactEmail = DataReaderObj.getFirmIncludedEmail();


        for (const email of emailInfos) {
            let emailData = {};
            if (dirtyFields.length > 1 || nonCustomizedEmailFields.includes(dirtyFields[0])) {
                emailData = {
                    appId: state.appId,
                    from: 'clientconnect@rts.lawyerdonedeal.com',
                    to: email,
                    subject: 'ClientConnect Fields Updated',
                    message: 'Your lawyer has added some new fields to your application. Please check your ClientConnect',
                    ccEmail: '',
                    bccEmail: (firmIncluded) ? firmContactEmail : '',
                    user: '',
                    type: 'generic',
                    shortCode: codeVal,
                    lawyerName: firmName
                };

            } else if (dirtyFields.length === 1) {
                emailData = {
                    appId: state.appId,
                    from: 'noreply@ldd.ca',
                    to: email,
                    subject: 'ClientConnect Fields Updated',
                    message: 'Your lawyer has added some new fields to your application. Please check your ClientConnect',
                    ccEmail: '',
                    bccEmail: (firmIncluded) ? firmContactEmail : '',
                    user: '',
                    type: dirtyFields[0], // if dirty fields length is 1, we only have one dashboard field that was changed.
                    shortCode: codeVal,
                    lawyerName: firmName
                };
            }

            try {
                await portalService.logEmail(state.appId, emailData);
                await portalService.sendEmail(state.appId, emailData, isNotaryFirm, language);
                await DataReaderObj.setDashboardFieldsNotDirty()
            } catch (error) {
                console.error(`Failed to notify client via email at ${email}`, error);
            }
        }
    };

    return (
        <>
            {notificationVisible === '1' && (
                <Alert variant="warning">
                    <span>Components were added to ClientConnect that your client(s) might not be aware of.</span>
                    <Button style={{marginLeft:'auto'}} onClick={handleNotifyClient} variant="warning">Notify Client</Button>
                </Alert>
            )}
            {emailLogs.length === 0 ? (
                <Alert variant="info">No emails logged yet</Alert>
            ) : (
                [...emailLogs].reverse().filter((log) => log.Subject && log.To).map((log, index) => {
                    const isDocumentShareLog = [
                        languageMap.documentShareNotificationSubject.EN,
                        languageMap.documentShareNotificationSubject.FR,
                        languageMap.documentShareNotificationSubject.zhCN
                    ].includes(log.Subject);
                    const sentPhrase = isDocumentShareLog ? 'sent to' : 'invitation sent to';
                    return (
                    <Alert key={index} variant="success">
                        {log.Subject} {sentPhrase} {log.To} on {new Date(log.rowKey.split('-')[1] * 1).toLocaleString()}
                    </Alert>
                    );
                })
            )}
        </>
    );
};

export default EmailLog;